import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Alert,
  Typography,
} from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import axios from 'axios';
import { acme, acmeBillingSectionContentSx } from '../../theme/acmeTheme';
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  Card,
  CardContent,
  Tabs,
  Tab,
  Input,
  Button,
} from '../ui';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import { invalidateTrackingConfig, refreshTrackingScripts } from '../../utils/trackingScripts';

const EMPTY_ANALYTICS = {
  googleAnalyticsId: '',
  googleTagManagerId: '',
  facebookPixelId: '',
  yandexMetrikaId: '',
  linkedInPartnerId: '',
  tikTokPixelId: '',
  microsoftClarityId: '',
  hotjarSiteId: '',
};

const ANALYTICS_FIELDS = [
  {
    key: 'googleAnalyticsId',
    label: 'Google Analytics ID',
    placeholder: 'G-XXXXXXXXXX',
    helperText: 'GA4 Measurement ID (format G-XXXXXXXXXX). Loads on every page for Google verification.',
    category: 'analytics',
  },
  {
    key: 'googleTagManagerId',
    label: 'Google Tag Manager ID',
    placeholder: 'GTM-XXXXXXX',
    helperText: 'Container ID (GTM-XXXXXXX). Also add a GA4 Configuration tag inside GTM if you use both.',
    category: 'analytics',
  },
  {
    key: 'yandexMetrikaId',
    label: 'Yandex Metrika ID',
    placeholder: '12345678',
    helperText: 'Numeric counter ID from Yandex Metrika.',
    category: 'analytics',
  },
  {
    key: 'microsoftClarityId',
    label: 'Microsoft Clarity ID',
    placeholder: 'abcdefghij',
    helperText: 'Project ID from Microsoft Clarity.',
    category: 'analytics',
  },
  {
    key: 'hotjarSiteId',
    label: 'Hotjar Site ID',
    placeholder: '1234567',
    helperText: 'Numeric Site ID from Hotjar.',
    category: 'analytics',
  },
  {
    key: 'facebookPixelId',
    label: 'Facebook Pixel ID',
    placeholder: '123456789012345',
    helperText: 'Meta Pixel ID for Facebook/Instagram ads tracking.',
    category: 'targeting',
  },
  {
    key: 'linkedInPartnerId',
    label: 'LinkedIn Insight Tag ID',
    placeholder: '1234567',
    helperText: 'Partner ID from LinkedIn Campaign Manager.',
    category: 'targeting',
  },
  {
    key: 'tikTokPixelId',
    label: 'TikTok Pixel ID',
    placeholder: 'CXXXXXXXXXXXXXXXXX',
    helperText: 'Pixel ID from TikTok Events Manager.',
    category: 'targeting',
  },
];

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const Settings = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [analytics, setAnalytics] = useState(EMPTY_ANALYTICS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get('/api/settings/analytics');
        setAnalytics({ ...EMPTY_ANALYTICS, ...(res.data.analytics || {}) });
      } catch (error) {
        console.error('Error fetching settings:', error);
        setErrorMessage('Failed to load settings.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [setErrorMessage]);

  const handleFieldChange = (key) => (event) => {
    setAnalytics((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put('/api/settings/analytics', analytics);
      setAnalytics({ ...EMPTY_ANALYTICS, ...(res.data.analytics || {}) });
      invalidateTrackingConfig();
      await refreshTrackingScripts();
      setSuccessMessage('Analytics settings saved successfully.');
    } catch (error) {
      console.error('Error saving settings:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const analyticsFields = ANALYTICS_FIELDS.filter((f) => f.category === 'analytics');
  const targetingFields = ANALYTICS_FIELDS.filter((f) => f.category === 'targeting');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageShell>
      <PageHeader title="Settings" description="Analytics and marketing tracking configuration." sx={{ mb: 2 }} />

      <Card sx={{ boxShadow: 'none' }}>
        <CardContent sx={acmeBillingSectionContentSx}>
          <PageBlockTitle gutterBottom>Settings</PageBlockTitle>
          <Tabs value={tabIndex} onChange={(_, value) => setTabIndex(value)}>
            <Tab icon={<AnalyticsIcon />} iconPosition="start" label="Analytics" />
          </Tabs>

          <TabPanel value={tabIndex} index={0}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Google tags load immediately on every page (before React) so Tag Assistant and GA verification can detect them.
              After saving, do a hard refresh (Ctrl+Shift+R) and run Google&apos;s check on the public homepage (mailsball.com).
              For GTM: create a <strong>GA4 Configuration</strong> tag inside your GTM container pointing to the same G- ID.
              Accept Analytics cookies in the banner so Consent Mode grants tracking.
            </Alert>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: acme.colors.foreground }}>
              Analytics &amp; Behavior
            </Typography>
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {analyticsFields.map((field) => (
                <Grid item xs={12} md={6} key={field.key}>
                  <Input
                    label={field.label}
                    placeholder={field.placeholder}
                    helperText={field.helperText}
                    value={analytics[field.key] || ''}
                    onChange={handleFieldChange(field.key)}
                  />
                </Grid>
              ))}
            </Grid>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: acme.colors.foreground }}>
              Marketing &amp; Targeting
            </Typography>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              {targetingFields.map((field) => (
                <Grid item xs={12} md={6} key={field.key}>
                  <Input
                    label={field.label}
                    placeholder={field.placeholder}
                    helperText={field.helperText}
                    value={analytics[field.key] || ''}
                    onChange={handleFieldChange(field.key)}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="default" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </TabPanel>
        </CardContent>
      </Card>

      <PageSnackbars {...snackbarProps} />
    </PageShell>
  );
};

export default Settings;
